// Feedback notes: Michael pins a note to whatever he's looking at in the app. A notes pass
// (the /nhub-notes-pass skill, usually run as a cloud routine) reads the open ones through the
// notes-api function, works on them, and answers each with a status and one line.
import type { PersonId } from './types.ts';

// Pure, so the app, the function and the tests share one set of rules.

/**
 * open: waiting for the next pass. addressed: a pass made the change, waiting on "ship".
 * shipped: live. closed: done with no code to ship (nothing needed, or not doing it).
 * needs_you: the pass asked something; answering puts it back to open, or a session that settled
 * it with Michael directly moves it on itself.
 */
export type NoteStatus = 'open' | 'addressed' | 'shipped' | 'closed' | 'needs_you';

/** What the note is about, read off the page when it was written. */
export interface NoteTarget {
  /** The screen's title ("Transactions"). */
  screen?: string;
  /** The picked element's accessible name, if it has one. */
  label?: string;
  /** Its visible text, trimmed. */
  text?: string;
  /** Where it sits, outermost first ("main > ul.txn-list > li.txn-row"). */
  where?: string;
}

export interface Note {
  id: string;
  createdAt: string;
  updatedAt: string;
  /** The screen it was written on, with whatever was open ("/money/transactions/t_123"). */
  route: string;
  target?: NoteTarget;
  body: string;
  /** Who wrote it. Notes from before this existed are Michael's. */
  author?: PersonId;
  status: NoteStatus;
  /** The pass's latest word: what changed, or its question. */
  reply?: string;
  /** Michael's answer to that question, or what's still wrong when he reopens it. */
  answer?: string;
}

/** Whose note it is (older ones have no author: Michael's). */
export const authorOf = (n: Pick<Note, 'author'>): PersonId => n.author ?? 'michael';

export const NOTE_STATUS_LABEL: Record<NoteStatus, string> = {
  open: 'Open',
  addressed: 'Addressed',
  shipped: 'Shipped',
  closed: 'Closed',
  needs_you: 'Needs you',
};

/** What a pass may change on a note. */
export interface NoteUpdate {
  id: string;
  status: 'addressed' | 'needs_you' | 'shipped' | 'closed';
  reply?: string;
}

export const MAX_REPLY = 500;

/** The notes a pass works on: open ones, and addressed ones an earlier pass never shipped. */
export function forPass(notes: readonly Note[]): Note[] {
  return notes.filter((n) => n.status === 'open' || n.status === 'addressed').sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** The note after a pass's update, or why the update was refused. */
export function applyUpdate(note: Note, u: NoteUpdate, now: string): Note | { refused: string } {
  const reply = u.reply?.trim();
  if (u.status === 'shipped') {
    if (note.status !== 'addressed') return { refused: `${note.id} is ${note.status}; only addressed notes can be shipped` };
    return { ...note, status: 'shipped', reply: reply || note.reply, updatedAt: now };
  }
  if (u.status !== 'addressed' && u.status !== 'needs_you' && u.status !== 'closed') return { refused: `${note.id}: unknown status ${String(u.status)}` };
  if (note.status === 'shipped' || note.status === 'closed') return { refused: `${note.id} is ${note.status}; it's finished` };
  if (!reply) return { refused: `${note.id}: say what changed, why it's closed, or ask the question` };
  if (reply.length > MAX_REPLY) return { refused: `${note.id}: reply is over ${MAX_REPLY} characters` };
  return { ...note, status: u.status, reply, updatedAt: now };
}

/** Michael answers the pass's question: back in the queue for the next pass. */
export function answerNote(note: Note, answer: string, now: string): Note {
  return { ...note, status: 'open', answer: answer.trim(), updatedAt: now };
}

/** Not right after all: back to open, with what's still wrong. */
export function reopenNote(note: Note, why: string, now: string): Note {
  return { ...note, status: 'open', answer: why.trim() || undefined, updatedAt: now };
}
